import { useState, useEffect, useCallback, useMemo } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Combobox } from '../../components/ui/Combobox';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { RowMenu } from '../../components/ui/RowMenu';
import { StatTile } from '../../components/ui/StatTile';
import { api } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { ROLE_LABELS, roleLabel, isSuperadmin, canManage, manageableRoles } from '../../lib/roles';

const initialsOf = (name, email) => {
  const s = (name || email || '?').trim();
  const parts = s.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase();
};

// Distinct styling per tier: superadmin (amber) > admin (brand) > reseller (slate).
const ROLE_BADGE_CLS = {
  superadmin: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  admin: 'bg-brand/15 text-brand-dark dark:bg-brand/25 dark:text-brand-light',
  reseller: 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300',
};

function RoleBadge({ role }) {
  const cls = ROLE_BADGE_CLS[role] || ROLE_BADGE_CLS.reseller;
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {roleLabel(role)}
    </span>
  );
}

function StatusPill({ status }) {
  const active = status === 'active';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${active ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300' : 'bg-slate-200 text-slate-500 dark:bg-white/10 dark:text-slate-400'}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-green-500' : 'bg-slate-400'}`} />
      {status}
    </span>
  );
}

export default function Users() {
  const { profile } = useAuth();
  // Only superadmins choose a role when creating; admins can only create resellers.
  const canPickRole = isSuperadmin(profile);
  // Roles this actor may assign (drives the Add-account picker), in tier order.
  const roleOptions = ['reseller', 'admin', 'superadmin']
    .filter((r) => manageableRoles(profile).includes(r))
    .map((r) => ({ value: r, label: ROLE_LABELS[r] }));

  const [resellerOptions, setResellerOptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // Add-account modal
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', password: '', resellerName: '', role: 'reseller' });
  const [busy, setBusy] = useState(false);

  // Edit modal
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ fullName: '', resellerName: '', password: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // Change-password modal
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  // Delete confirm modal
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Toolbar filters
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get('/api/users'), api.get('/api/users/reseller-options')])
      .then(([u, r]) => {
        setUsers(u);
        setResellerOptions(r.map((x) => x.name));
      })
      .catch((e) => setMsg({ type: 'error', text: e.message }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.status === 'active').length,
    inactive: users.filter((u) => u.status !== 'active').length,
    resellers: users.filter((u) => u.role === 'reseller').length,
  }), [users]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (!q) return true;
      return [u.full_name, u.email, u.reseller_name].some((f) => String(f || '').toLowerCase().includes(q));
    });
  }, [users, query, statusFilter]);

  const openAdd = () => {
    setMsg(null);
    setForm({ fullName: '', email: '', password: '', resellerName: '', role: 'reseller' });
    setAdding(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/users/create', form);
      setMsg({ type: 'success', text: `Account created for ${form.email}` });
      setAdding(false);
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (user) => {
    const next = user.status === 'active' ? 'inactive' : 'active';
    try {
      await api.patch(`/api/users/${user.id}/status`, { status: next });
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const openEdit = (user) => {
    setMsg(null);
    setEditForm({ fullName: user.full_name || '', resellerName: user.reseller_name || '', password: '' });
    setEditing(user);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.patch(`/api/users/${editing.id}`, {
        fullName: editForm.fullName,
        ...(editing.role === 'reseller' ? { resellerName: editForm.resellerName } : {}),
      });
      if (editForm.password) {
        await api.patch(`/api/users/${editing.id}/password`, { password: editForm.password });
      }
      setMsg({ type: 'success', text: `Updated ${editForm.fullName || editing.email}` });
      setEditing(null);
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSavingEdit(false);
    }
  };

  const openPassword = (user) => {
    setMsg(null);
    setPwValue('');
    setPwUser(user);
  };

  // Admin-set a new password for any account (no email — hand it to them directly).
  const savePassword = async (e) => {
    e.preventDefault();
    if (pwValue.length < 8) {
      setMsg({ type: 'error', text: 'Password must be at least 8 characters.' });
      return;
    }
    setPwBusy(true);
    try {
      await api.patch(`/api/users/${pwUser.id}/password`, { password: pwValue });
      setMsg({ type: 'success', text: `Password updated for ${pwUser.full_name || pwUser.email}.` });
      setPwUser(null);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setPwBusy(false);
    }
  };

  // Wipe a user's authenticator setup — their next login shows the QR again.
  const doResetMfa = async (user) => {
    setMsg(null);
    try {
      await api.post(`/api/users/${user.id}/reset-mfa`);
      setMsg({ type: 'success', text: `2FA reset for ${user.full_name || user.email} — they'll set it up again at their next login.` });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.del(`/api/users/${confirmDelete.id}`);
      setMsg({ type: 'success', text: `Deleted ${confirmDelete.full_name || confirmDelete.email}` });
      setConfirmDelete(null);
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const inputCls = 'rounded-lg border border-slate-200 dark:border-white/15 bg-white/70 dark:bg-slate-800/70 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-brand focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brand/30';

  const columns = [
    {
      key: 'full_name', label: 'Account',
      render: (_v, row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand/15 text-xs font-semibold text-brand-dark dark:bg-brand/25 dark:text-brand-light">
            {initialsOf(row.full_name, row.email)}
          </span>
          <div className="min-w-0">
            <div className="truncate font-medium text-navy-800 dark:text-slate-100">{row.full_name || '—'}</div>
            <div className="truncate text-xs text-slate-400 dark:text-slate-500">{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'reseller_name', label: 'Reseller',
      render: (v, row) => (row.role !== 'reseller'
        ? <span className="text-slate-600 dark:text-slate-300">All</span>
        : v ? <span className="text-slate-600 dark:text-slate-300">{v}</span>
          : <span className="text-slate-400 dark:text-slate-600">—</span>),
    },
    { key: 'role', label: 'Role', render: (v) => <RoleBadge role={v} /> },
    { key: 'status', label: 'Status', render: (v) => <StatusPill status={v} /> },
    {
      key: 'id', label: '', align: 'right',
      render: (_v, row) => {
        // The actor can only touch accounts they're allowed to manage; superadmin
        // accounts are additionally protected from deactivation/deletion.
        const manageable = canManage(profile, row.role);
        const isTopTier = row.role === 'superadmin';
        return (
          <div className="flex justify-end">
            <RowMenu
              emptyLabel="You don’t have permission to manage this account."
              items={[
                {
                  label: 'Edit details',
                  hidden: !manageable,
                  icon: <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M13.5 3.5l3 3L7 16H4v-3l9.5-9.5z" strokeLinejoin="round" /></svg>,
                  onClick: () => openEdit(row),
                },
                {
                  label: 'Change password',
                  hidden: !manageable,
                  icon: <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="7" cy="10" r="3" /><path d="M9.5 9.5L17 9.5M14.5 9.5v2.5M17 9.5V12" strokeLinecap="round" strokeLinejoin="round" /></svg>,
                  onClick: () => openPassword(row),
                },
                {
                  label: 'Reset 2FA',
                  hidden: !manageable,
                  icon: <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10 2.5l6 2.5v4c0 3.6-2.4 6.7-6 8-3.6-1.3-6-4.4-6-8V5l6-2.5z" strokeLinejoin="round" /><path d="M7.5 10l1.8 1.8L12.8 8.3" strokeLinecap="round" strokeLinejoin="round" /></svg>,
                  onClick: () => doResetMfa(row),
                },
                {
                  label: row.status === 'active' ? 'Deactivate' : 'Activate',
                  hidden: !manageable || isTopTier,
                  icon: row.status === 'active'
                    ? <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10 3v6" strokeLinecap="round" /><path d="M6 5.2a6 6 0 1 0 8 0" strokeLinecap="round" /></svg>
                    : <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M4 10.5l3.5 3.5L16 6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
                  onClick: () => toggleStatus(row),
                },
                {
                  label: 'Delete account',
                  danger: true,
                  hidden: !manageable || isTopTier,
                  icon: <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M4 6h12M8 6V4h4v2M6.5 6l.7 10h5.6l.7-10" strokeLinecap="round" strokeLinejoin="round" /></svg>,
                  onClick: () => setConfirmDelete(row),
                },
              ]}
            />
          </div>
        );
      },
    },
  ];

  return (
    <AppShell title="User Management">
      {msg && (
        <div className={`rounded-lg px-4 py-2 text-sm ${msg.type === 'error' ? 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300' : 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300'}`}>
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatTile label="Total accounts" value={stats.total} />
        <StatTile label="Active" value={stats.active} />
        <StatTile label="Inactive" value={stats.inactive} />
        <StatTile label="Resellers" value={stats.resellers} />
      </div>

      <Card
        title="Manage accounts"
        actions={(
          <Button onClick={openAdd} className="gap-1.5">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 4v12M4 10h12" strokeLinecap="round" /></svg>
            Add account
          </Button>
        )}
      >
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="9" cy="9" r="6" /><path d="M14 14l3 3" strokeLinecap="round" /></svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, or reseller…"
              className={`${inputCls} w-full pl-9`}
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputCls}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {loading ? <Spinner /> : <Table columns={columns} rows={filtered} pageSize={10} empty="No accounts match your filters." />}
      </Card>

      {/* Add account modal */}
      <Modal open={adding} onClose={() => setAdding(false)} title="Add account">
        <form onSubmit={submit} className="space-y-4">
          <Input label="Full name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} required />
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <Input label="Password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          {canPickRole && (
            <Select
              label="Role"
              options={roleOptions}
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value, resellerName: '' })}
            />
          )}
          {form.role === 'reseller' && (
            <Combobox label="Reseller" placeholder="Select reseller…" value={form.resellerName} onChange={(val) => setForm({ ...form, resellerName: val })} options={resellerOptions} searchPlaceholder="Search reseller…" />
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => setAdding(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create account'}</Button>
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500">No email is sent — hand the credentials to the user directly.</p>
        </form>
      </Modal>

      {/* Edit modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit ${editing?.full_name || editing?.email || 'user'}`}>
        <form onSubmit={saveEdit} className="space-y-4">
          <Input label="Full name" value={editForm.fullName} onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })} required />
          {editing?.role === 'reseller' && (
            <Combobox label="Reseller" placeholder="Select reseller…" value={editForm.resellerName} onChange={(val) => setEditForm({ ...editForm, resellerName: val })} options={resellerOptions} searchPlaceholder="Search reseller…" />
          )}
          <Input label="New password" type="text" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Leave blank to keep current" />
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="submit" disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save changes'}</Button>
          </div>
        </form>
      </Modal>

      {/* Change password modal */}
      <Modal open={!!pwUser} onClose={() => setPwUser(null)} title={`Change password — ${pwUser?.full_name || pwUser?.email || ''}`}>
        <form onSubmit={savePassword} className="space-y-4">
          <Input label="New password" type="text" value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="At least 8 characters" required />
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => setPwUser(null)}>Cancel</Button>
            <Button type="submit" disabled={pwBusy}>{pwBusy ? 'Updating…' : 'Update password'}</Button>
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500">No email is sent — hand the new password to the user directly.</p>
        </form>
      </Modal>

      {/* Delete confirm modal */}
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete account">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Permanently delete <strong className="text-navy-800 dark:text-slate-100">{confirmDelete?.full_name || confirmDelete?.email}</strong>?
          This removes their sign-in and profile and <strong>cannot be undone</strong>.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button type="button" disabled={deleting} onClick={doDelete} className="!bg-red-600 !text-white !shadow-none hover:!bg-red-700">
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </AppShell>
  );
}
